/* eslint-disable no-undef */
import { Card } from "@coherence-design-system/controls";
import { IBreadcrumbItem, IBreadcrumbProps, PrimaryButton } from "@fluentui/react";
import { Stack } from "@mui/material";
import CryptoJS from "crypto-js";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CustomBreadcrumb } from "../SharedComponents/CustomBreadcrumb";
import { FileViewer } from "../SharedComponents/FileViewer";

interface Document {
  description: string;
  fileType: "pdf" | "docx";
  name: string;
  url: string;
}

const cryptoEncryptionKey = process.env.REACT_APP_CRYPTOJS_ENCRYPTION_KEY!;

const decryptParams = (encryptedParams: string) => {
  return CryptoJS.AES.decrypt(encryptedParams, cryptoEncryptionKey).toString(CryptoJS.enc.Utf8);
};

const breadcrumbStyles = {
  root: {
    fontSize: "14px",
    margin: 0,
    paddingLeft: "8px",
    "--itemLink-color": "rgb(0, 120, 212)"
  },
  item: {
    fontSize: "inherit"
  },
  itemLink: {
    fontSize: "inherit",
    color: "var(--itemLink-color)",
    ":hover": {
      background: "none",
      textDecoration: "underline",
      color: "var(--itemLink-color)"
    }
  }
};

export const DataResearchDocumentViewer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const encryptedData = searchParams.get("id");
  const [data, setData] = useState<Document>();
  const [items, setItems] = useState<IBreadcrumbItem[]>([
    { text: "Home", key: "home", onClick: () => navigate("/") },
    { text: "Data Research", key: "dataResearch", onClick: () => navigate("/dataresearch") }
  ]);

  const breadcrumbProps: IBreadcrumbProps = {
    items: items,
    styles: breadcrumbStyles
  };

  useEffect(() => {
    const decryptedData = decryptParams(encryptedData ?? "");

    try {
      const parsedData = JSON.parse(decryptedData) as Document;
      setData(parsedData);
      setItems([...items, { text: parsedData.name, key: "dataResearchworkerviewer", isCurrentItem: true }]);
    } catch (error) {
      console.error("Failed to parse decrypted data", error);
    }
  }, []);

  const handleDownloadClick = () => {
    if (data?.url) {
      const link = document.createElement("a");
      link.href = data.url;
      link.download = data.url.split("/").pop() ?? "download.pdf";
      link.click();
    }
  };

  const handleCloseClick = () => {
    navigate("/dataresearch");
  };

  return (
    <div style={{ marginBlockStart: 10, marginInline: 10 }}>
      <Card
        header={
          <Stack
            justifyContent="space-between"
            direction={{ xs: "column", sm: "column", md: "row" }}
            spacing={{ xs: 1, sm: 2, md: 3 }}
          >
            <div style={{ flex: 1 }}>
              <CustomBreadcrumb {...breadcrumbProps} />
            </div>
            <Stack direction={{ xs: "column", sm: "column", md: "row" }} spacing={{ xs: 1, sm: 2, md: 3 }}>
              <PrimaryButton
                iconProps={{ iconName: "Download", styles: { root: { fontSize: 15 } } }}
                onClick={handleDownloadClick}
                styles={{ flexContainer: { flexDirection: "row-reverse" } }}
                text="Download"
                title="Download"
              />
              <PrimaryButton
                iconProps={{ iconName: "ChromeClose", styles: { root: { fontSize: 10 } } }}
                onClick={handleCloseClick}
                styles={{ flexContainer: { flexDirection: "row-reverse" } }}
                text="Close"
                title="Close"
              />
            </Stack>
          </Stack>
        }
        styles={{ layout: { padding: "0 !important" } }}
      >
        <FileViewer
          fileUrl={data?.url ?? ""}
          fileType={data?.fileType ?? "pdf"}
          frameStyle={{ height: "calc(100vh - 105px)", width: "100%", border: 0 }}
        />
      </Card>
    </div>
  );
};
